// Pure, dependency-free role→permission contract. Imported by BOTH the tRPC
// server (server/api/trpc.ts) and the client (providers/userProvider.tsx).
// MUST stay free of React / auth0 / env / DB imports to remain bundle-safe for
// both sides and avoid circular deps. Roles arrive via the
// `https://app.hakio.com/roles` JWT claim as an array of strings.
// Mirrored in Python at `ds_api/src/ds_api/core/roles.py` — change both together;
// roles.contract.test.ts pins the two in sync.

/** The Auth0 custom JWT claim carrying the user's role strings. Single source
 * of truth for the claim key — do not hard-code the literal elsewhere. */
export const ROLES_CLAIM = "https://app.hakio.com/roles"

/** Read the roles array off any object that may carry the Auth0 roles claim
 * (a decoded JWT payload, a tRPC session, or an Auth0 user). Returns `[]` when
 * the claim is absent or malformed. */
export function rolesFromClaim(src: { [ROLES_CLAIM]?: unknown } | null | undefined): string[] {
  const roles = src?.[ROLES_CLAIM]
  return Array.isArray(roles) ? (roles as string[]) : []
}

/** Mutually-exclusive customer-facing roles. */
export const ROLE = {
  READ_ONLY: "read_only",
  USER: "user",
  ADMIN: "admin",
} as const

/** Hakio-staff role. `hakio` is the role currently defined & assigned in Auth0;
 * `staff` is also accepted so the canonical string can be adopted later without
 * a code change. Both map to isStaff. */
export const STAFF_ROLE = {
  STAFF: "staff",
  /** The staff role currently assigned in Auth0. */
  HAKIO: "hakio",
} as const

export interface Permissions {
  canEdit: boolean
  canAdminister: boolean
  /** True if any staff role (`staff` or legacy `hakio`) is present. */
  isStaff: boolean
}

const STAFF_ROLES = new Set<string>(Object.values(STAFF_ROLE))

/** True for any staff role string (`staff` or legacy `hakio`). */
export function isStaffRole(role: string): boolean {
  return STAFF_ROLES.has(role)
}

/** True if a JWT roles claim (a `string[]`) contains any staff role. Accepts
 * `undefined` for defensive call sites reading a possibly-absent claim. */
export function hasStaffRole(roles: string[] | undefined): boolean {
  return roles?.some(isStaffRole) ?? false
}

/**
 * Effective permissions for a role claim, resolved as a SINGLE access tier
 * (highest wins). Exactly one tier applies per user:
 *
 *   read_only              → { canEdit: false, canAdminister: false, isStaff: false }
 *   user (or no tier role) → { canEdit: true,  canAdminister: false, isStaff: false }
 *   admin                  → { canEdit: true,  canAdminister: true,  isStaff: false }
 *   hakio / staff          → { canEdit: true,  canAdminister: true,  isStaff: true  }
 *
 * Default is `user`: a claim with no recognized tier role is editable; only an
 * explicit `read_only` removes edit access. (`agent` is a capability flag
 * handled elsewhere, not a tier.) The "no role = user" default mirrors current
 * frontend behavior and is expected to change in a later ticket.
 */
export function permissionsForRoles(roles: string[]): Permissions {
  if (roles.some(isStaffRole)) {
    return { canEdit: true, canAdminister: true, isStaff: true }
  }
  if (roles.includes(ROLE.ADMIN)) {
    return { canEdit: true, canAdminister: true, isStaff: false }
  }
  if (roles.includes(ROLE.READ_ONLY)) {
    return { canEdit: false, canAdminister: false, isStaff: false }
  }
  return { canEdit: true, canAdminister: false, isStaff: false }
}

export const EDIT_FORBIDDEN_MESSAGE = "Read-only users cannot modify data."
export const FORBIDDEN_MESSAGE = "You do not have permission to perform this action."

/** HTTP header (and SSE connectionParams key) carrying a staff user's simulated
 * role. Untrusted input: the server honors it only for callers whose REAL JWT
 * claim is staff, and only as a downgrade to a customer tier. */
export const SIMULATED_ROLE_HEADER = "x-hakio-simulated-role"

/** Roles a staff user may simulate — customer tiers only. `staff`/`hakio`
 * (and capability flags like `agent`) are never simulatable: simulation is a
 * downgrade-only channel and must not be able to grant anything. */
const SIMULATABLE_ROLES = [ROLE.READ_ONLY, ROLE.USER, ROLE.ADMIN] as const

export type SimulatableRole = (typeof SIMULATABLE_ROLES)[number]

/** Parse an untrusted header/storage value into a simulatable role, else
 * `null` (= not simulating). Staff roles, unknown strings, and non-strings all
 * return `null`, so malformed input fails safe to the caller's real roles. */
export function parseSimulatedRole(value: unknown): SimulatableRole | null {
  return typeof value === "string" && (SIMULATABLE_ROLES as readonly string[]).includes(value)
    ? (value as SimulatableRole)
    : null
}

/**
 * The single validation point for role simulation. Honors `requestedRole` only
 * when the REAL claim is staff and the request is a valid customer-tier
 * downgrade; otherwise returns the real roles untouched.
 *
 * Returns REPLACEMENT semantics (`[role]`, never `realRoles.concat(role)`) —
 * `permissionsForRoles` is highest-tier-wins, so concatenating would let the
 * staff role win and make simulation a no-op.
 */
export function resolveEffectiveRoles(
  realRoles: string[],
  requestedRole: string | null | undefined,
): { effectiveRoles: string[]; simulatedRole: SimulatableRole | null } {
  const simulatedRole = parseSimulatedRole(requestedRole)
  if (!simulatedRole || !hasStaffRole(realRoles)) {
    return { effectiveRoles: realRoles, simulatedRole: null }
  }
  return { effectiveRoles: [simulatedRole], simulatedRole }
}
